import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Step, useStepper } from "@mirage/hooks/useStepper";
import { PrePpWlApplicantDetailsFormStep } from "@prePublicPortalWestLindsay/pages/form/applicantDetailsFormStep/PrePpWlApplicantDetailsFormStep";
import { PrePpWlAgentDetailsFormStep } from "@prePublicPortalWestLindsay/pages/form/agentDetailsFormStep/PrePpWlAgentDetailsFormStep";
import { ErrorSummary, H1, LoadingBox, Main } from "govuk-react";
import { InitialFormState } from "./utils/initialState";
import { FormProvider } from "./hooks/formContext";
import { PrePpWlDescriptionOfDevelopmentStep } from "./descriptionOfDevelopmentFormStep/PrePpWlDescriptionOfDevelopmentStep";
import { PrePpWlSubmitStep } from "./submitFormStep/PrePpWlSubmitStep";
import { PrePpWlSiteDetailsStep } from "./siteDetailsFormStep/PrePpWlSiteDetailsStep";
import { PrePpWlStageStep } from "./stageFormStep/PrePpWlStageStep";
import { PrePpWlAdditionalInformationStep } from "./additionalInformationFormStep/PrePpWlAdditionalInformationStep";
import { useLocation } from "react-router-dom";
import { PrePublicPortalWLRoutingService } from "@prePublicPortalWestLindsay/services/PrePublicPortalWLRoutingService";
import { PrePpWlTypeOfDevelopmentStep } from "./typeOfDevelopmentStep/PrePpWlTypeOfDevelopmentStep";
import { IPrePpWlForm, IPrePpWlFormField, PrePpWlFormKeyToLabel } from "@shared/prePublicPortalWestLindsay/contracts/IPrePpWlForm";
import { PrPpWlFormManager } from "@shared/prePublicPortalWestLindsay/managers/PrePpWlFormManager";
import { CalculateFeeRequest, PrePpWlFormFeeCalculator, WlPriceListJson } from "@shared/prePublicPortalWestLindsay/utils/PrePpWlFormFeeCalculator";
import { ErrorSummaryError } from "@publicMirage/contracts/GovReact";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { scrollToTop } from "@mirage/components/helpers/windowHelper";
import { GovFieldContentType, GovFieldProps } from "@publicMirage/components/govReact/GovField";
import { cloneDeep } from "lodash";
import PrePublicPortalWlSettingsService from "@prePublicPortalWestLindsay/services/PrePublicPortalWlSettingsService";

const router = new PrePublicPortalWLRoutingService();
const PrePublicPortalWlFormStepElements = [
    { key: router.ApplicantDetails, Component: PrePpWlApplicantDetailsFormStep, path: router.FormApplicantDetailsRoute() },
    { key: router.AgentDetails, Component: PrePpWlAgentDetailsFormStep, path: router.FormAgentDetailsRoute() },
    { key: router.DescriptionOfDevelopment, Component: PrePpWlDescriptionOfDevelopmentStep, path: router.FormDescriptionOfDevelopmentRoute() },
    { key: router.TypeOfDevelopment, Component: PrePpWlTypeOfDevelopmentStep, path: router.FormTypeOfDevelopmentRoute() },
    { key: router.SiteDetails, Component: PrePpWlSiteDetailsStep, path: router.FormSiteDetailsRoute() },
    { key: router.Stage, Component: PrePpWlStageStep, path: router.FormStageRoute() },
    { key: router.AdditionalInformation, Component: PrePpWlAdditionalInformationStep, path: router.FormAdditionalInformationRoute() },
    { key: router.Confirm, Component: PrePpWlSubmitStep, path: router.FormConfirmRoute() },
] as const;
type PrePublicPortalWlFormSteps = typeof PrePublicPortalWlFormStepElements;
type Union = typeof PrePublicPortalWlFormStepElements[number];

const settingsService = new PrePublicPortalWlSettingsService();

export const PrePublicPortalWlForm = memo(() => {
    const location = useLocation();
    const availableSteps = Object.values(router.PrePublicPortalWlFormSteps);
    const currentPathIsStep = availableSteps.includes(location.pathname.split("/").pop() as typeof PrePublicPortalWlFormStepElements[number]["key"]);
    const currentPathStep = currentPathIsStep ? (location.pathname.split("/").pop() as typeof PrePublicPortalWlFormStepElements[number]["key"]) : router.ApplicantDetails;
    const [formState, setFormState] = useState(cloneDeep(InitialFormState));
    const [priceList, setPriceList] = useState<WlPriceListJson | null>(null);
    const [priceListLoading, setPriceListLoading] = useState(true);
    const navigate = useStableNavigate();
    const [CurrentStep, { is, next: setNextStep, previous: setPreviousStep, set, first }] = useStepper<Union, PrePublicPortalWlFormSteps>(
        currentPathStep,
        PrePublicPortalWlFormStepElements
    );
    const getStepPath = useCallback((step: Step) => PrePublicPortalWlFormStepElements.find((s) => s.key === step.key)?.path, []);
    const formErrors: ErrorSummaryError[] = useMemo(() => {
        const keys = Object.keys(formState) as (keyof IPrePpWlForm)[];
        return keys
            .map((key) => {
                if (!formState[key].errorMsg) {
                    return null;
                }
                let targetName = key;
                if (key === "applicantAddress" || key === "siteLocationAddress") {
                    targetName = `${key}Line1`;
                }
                return {
                    targetName,
                    text: `${PrePpWlFormKeyToLabel[key]} - ${formState[key].errorMsg}`,
                };
            })
            .filter(Boolean) as ErrorSummaryError[];
    }, [formState]);
    const calculateFeeData: CalculateFeeRequest = useMemo(() => {
        return {
            additionalGrossFloorSpace: formState.additionalGrossFloorSpace.value,
            nonResidentialChangeOfUse: formState.nonResidentialChangeOfUse.value,
            serviceLevel: formState.developmentScale.value,
            totalNumberOfUnitsProposed: formState.totalNumberOfUnitsProposed.value,
            typeOfDevelopment: formState.typeOfDevelopment.value,
        };
    }, [formState.additionalGrossFloorSpace, formState.nonResidentialChangeOfUse, formState.developmentScale, formState.totalNumberOfUnitsProposed, formState.typeOfDevelopment]);
    const previous = useCallback(() => {
        const previousStep = setPreviousStep();
        const stepPath = getStepPath(previousStep);
        if (stepPath) {
            navigate(stepPath);
        }
        if (previousStep.key === router.DescriptionOfDevelopment) {
            PrPpWlFormManager.clearTypeOfDevelopmentFields(formState);
        }
    }, [formState, getStepPath, navigate, setPreviousStep]);
    const next = useCallback(() => {
        const nextStep = setNextStep();
        const stepPath = getStepPath(nextStep);
        if (stepPath) {
            navigate(stepPath);
        }
    }, [getStepPath, navigate, setNextStep]);

    useEffect(() => {
        first();
        navigate(PrePublicPortalWlFormStepElements[0].path, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const stepWithPathExists = PrePublicPortalWlFormStepElements.some((step) => step.path === location.pathname);
        if (CurrentStep.key !== currentPathStep && stepWithPathExists) {
            set(currentPathStep);
        }
        setFormState((p) => {
            const copyOfState = { ...p };
            PrPpWlFormManager.resetErrorMsg(copyOfState);
            return copyOfState;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPathStep]);

    useEffect(() => {
        if (CurrentStep.path !== location.pathname) {
            navigate(CurrentStep.path);
            scrollToTop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CurrentStep]);

    useEffect(() => {
        const getPriceList = async () => {
            setPriceListLoading(true);
            const priceList = await settingsService.getPriceList();
            if (!priceList) {
                setPriceListLoading(false);
                return;
            }
            setPriceListLoading(false);
            setPriceList(priceList);
        };
        getPriceList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!priceList || CurrentStep.key !== router.PrePublicPortalWlFormSteps.CONFIRM) {
            return;
        }
        const feeResult = PrePpWlFormFeeCalculator.calculateFee(calculateFeeData, priceList);
        setFieldValue("isCustomFee", feeResult.isCustomFee);
        setFieldValue("feeAmount", feeResult.fee);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CurrentStep.key]);

    if ("scrollRestoration" in history) {
        history.scrollRestoration = "manual";
    }

    const nextActionWithValidation = useCallback(
        (validator: (record: IPrePpWlForm) => IPrePpWlForm) => {
            const formAfterValidation = validator(formState);
            const isFormValid = PrPpWlFormManager.isFormValid(formAfterValidation);
            setFormState({ ...formAfterValidation });
            if (isFormValid) {
                next();
            }
        },
        [formState, next]
    );
    const applicantNextAction = useCallback(() => nextActionWithValidation((r) => PrPpWlFormManager.validateApplicantDetailsStep(r)), [nextActionWithValidation]);
    const agentNextAction = useCallback(() => nextActionWithValidation((r) => PrPpWlFormManager.validateAgentDetailsStep(r)), [nextActionWithValidation]);
    const descriptionOfDevelopmentNextAction = useCallback(
        () => nextActionWithValidation((r) => PrPpWlFormManager.validateDescriptionOfDevelopmentStep(r)),
        [nextActionWithValidation]
    );

    const errorSummaryHeader = "Some fields are incorrect";
    const typeOfDevelopmentNextAction = useCallback(() => nextActionWithValidation((r) => PrPpWlFormManager.validateTypeOfDevelopmentStep(r)), [nextActionWithValidation]);
    const siteNextAction = useCallback(() => nextActionWithValidation((r) => PrPpWlFormManager.validateSiteDetails(r)), [nextActionWithValidation]);
    const stageNextAction = useCallback(() => nextActionWithValidation((r) => PrPpWlFormManager.validateStageStep(r)), [nextActionWithValidation]);
    const additionalInformationNextAction = useCallback(() => nextActionWithValidation((r) => PrPpWlFormManager.validateAdditionalInformationStep(r)), [nextActionWithValidation]);
    const setFieldValue = useCallback((fieldName: keyof IPrePpWlForm, value: any) => {
        setFormState((prevState) => ({
            ...prevState,
            [fieldName]: {
                ...prevState[fieldName],
                value,
            },
        }));
    }, []);
    const updateFormFieldValue = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, value: any) => {
            const fieldName = event.target.name as keyof IPrePpWlForm;
            setFieldValue(fieldName, value);
        },
        [setFieldValue]
    );

    const getFieldProp = useCallback(
        <FieldName extends keyof IPrePpWlForm, Prop extends keyof IPrePpWlFormField<IPrePpWlForm[FieldName]>>(fieldName: FieldName, prop: Prop): IPrePpWlFormField[Prop] =>
            formState[fieldName][prop] as IPrePpWlFormField[Prop],
        [formState]
    );
    const toFieldProps = useCallback(
        (fieldName: keyof IPrePpWlForm, contentType: GovFieldContentType, radioValue?: string): GovFieldProps => {
            return {
                inputName: fieldName,
                value: getFieldProp(fieldName, "value"),
                label: PrePpWlFormKeyToLabel[fieldName],
                contentType,
                onChange: updateFormFieldValue,
                errorMsg: getFieldProp(fieldName, "errorMsg"),
                radioValue,
                required: PrPpWlFormManager.isFieldRequired(formState, fieldName),
            };
        },
        [getFieldProp, updateFormFieldValue, formState]
    );
    const onHandleErrorClick = useCallback((targetName: string) => {
        const element = document.querySelector(`[name=${targetName}]`);
        element?.scrollIntoView({ behavior: "smooth" });
    }, []);

    return (
        <Main>
            <H1>Apply for pre-application advice</H1>
            {formErrors.length ? <ErrorSummary heading={errorSummaryHeader} errors={formErrors} onHandleErrorClick={onHandleErrorClick} /> : null}
            <FormProvider value={{ formState, getFieldProp, toFieldProps, setFieldValue, previous, setFormState }}>
                <LoadingBox loading={priceListLoading}>{getStep()}</LoadingBox>
            </FormProvider>
        </Main>
    );

    function getStep() {
        if (priceListLoading) {
            return null;
        }
        if (!priceList) {
            return <ErrorSummary heading="Service unavailable" description="We're sorry, but we can't calculate the prices right now. Please check back later." />;
        }
        if (is(CurrentStep, router.PrePublicPortalWlFormSteps.APPLICANT_DETAILS)) {
            return <CurrentStep.Component next={applicantNextAction} />;
        }
        if (is(CurrentStep, router.PrePublicPortalWlFormSteps.AGENT_DETAILS)) {
            return <CurrentStep.Component next={agentNextAction} />;
        }
        if (is(CurrentStep, router.PrePublicPortalWlFormSteps.DESCRIPTION_OF_DEVELOPMENT)) {
            return <CurrentStep.Component next={descriptionOfDevelopmentNextAction} />;
        }
        if (is(CurrentStep, router.PrePublicPortalWlFormSteps.TYPE_OF_DEVELOPMENT)) {
            return <CurrentStep.Component next={typeOfDevelopmentNextAction} />;
        }
        if (is(CurrentStep, router.PrePublicPortalWlFormSteps.SITE_DETAILS)) {
            return <CurrentStep.Component next={siteNextAction} />;
        }
        if (is(CurrentStep, router.PrePublicPortalWlFormSteps.STAGE)) {
            return <CurrentStep.Component next={stageNextAction} />;
        }
        if (is(CurrentStep, router.PrePublicPortalWlFormSteps.ADDITIONAL_INFORMATION)) {
            return <CurrentStep.Component next={additionalInformationNextAction} />;
        }
        if (is(CurrentStep, router.PrePublicPortalWlFormSteps.CONFIRM)) {
            return <CurrentStep.Component />;
        }
        throw Error("You did something wrong - there may be additional unsupported step!");
    }
});

export interface PrePpWlFormComponentStepProps {
    next: () => void;
}
